class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

class QuantityInputDawn extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input-dawn', QuantityInputDawn);

class ProductVariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {

      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');

      // change previous target.selectedOptions with new value
      const newSelectedOption = Array.from(target.options).find((option) => option.value === value);
      if (newSelectedOption) {
        newSelectedOption.setAttribute('selected', 'selected');
      }

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .product-option__swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('product-option__swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('product-option__swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}
customElements.define('product-variant-selects', ProductVariantSelects);

const PDP_VARIANTS_DEBUG = Boolean(window.Shopify?.designMode);

const isZeroPriceVariant = (variant) => !variant || Number(variant.price) === 0;

if (!customElements.get('product-info')) {
  customElements.define(
    "product-info",
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector(".quantity__input");
        this.productMediaGallery = this.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`);
        this.productVariantSelects = this.querySelector('product-variant-selects');
      }

      debugLog(message, payload = {}) {
        if (!PDP_VARIANTS_DEBUG) return;
        console.log(`[PDP-VARIANTS][${this.dataset.section}] ${message}`, payload);
      }

      connectedCallback() {
        this.initializeProductSwapUtility();
        this.debugLog('connectedCallback', {
          hasMediaGallery: Boolean(this.productMediaGallery),
          hideOtherVariantsMedia: this.productMediaGallery?.dataset?.hideOtherVariantsMedia
        });

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(
          new CustomEvent("product-info:loaded", { bubbles: true })
        );
        this.initUpdateMedia();
        this.initSaleSkuBadge();
        this.updateAddToCartVisibility(this.getSelectedVariant(this));
        this.updateModalMciPricing(this.getSelectedVariant(this)?.price, this.getSelectedVariant(this)?.compare_at_price);

        if (this.productMediaGallery && this.productMediaGallery.dataset.hideOtherVariantsMedia === 'true') {
          const filteredMediaId = this.updateVariantMedia();
          if (filteredMediaId) {
            this.updateMedia(filteredMediaId);
          }
        }

        this.updatePdpGalleryEntregaTag(this.getSelectedVariant(this)?.id);
        this.updatePdpTitleForColorVariant(this.getSelectedVariant(this));

        const selectedVariant = this.getSelectedVariant(this);
        if (selectedVariant?.id) {
          this.updateVisualStockBlocks(selectedVariant.id);
        }
      }

      initSaleSkuBadge() {
        this._saleSkuBadges = Array.from(this.querySelectorAll("[data-sale-sku-badge]"));
        if (!this._saleSkuBadges.length) return;
        this._saleBadgeSkuSet = this.getSaleBadgeSkuSet();
        const selectedVariant = this.getSelectedVariant(this);
        this.updateVariantSaleBadge(selectedVariant?.sku);
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector(
          ".product-form__quantity"
        );
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      initUpdateMedia() {
        const variantIdFromUrl = parseInt(window.location.search.split('?').filter(item => item.includes('variant='))[0]?.split('=')[1]) || null;
        if (!variantIdFromUrl) { return; }
        const variant = this.getSelectedVariant(this);
        if (!variant || !variantIdFromUrl) { return; }
        if (variant?.id === variantIdFromUrl) {
          this.updateMedia(variant?.featured_media?.id);
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push(html =>
          html
            .querySelectorAll(".scroll-trigger")
            .forEach(element =>
              element.classList.add("scroll-trigger--cancel")
            )
        );
        this.postProcessHtmlCallbacks.push(newNode => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({
        data: { event, target, selectedOptionValues }
      }) {
        if (!this.contains(event.target)) return;
        this.debugLog('handleOptionValueChange', {
          targetId: target?.id,
          selectedOptionValues
        });

        this.resetProductFormState();

        const productUrl =
          target.dataset.productUrl ||
          this.pendingRequestUrl ||
          this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage =
          this.dataset.updateUrl === "true" && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(
            productUrl,
            selectedOptionValues,
            shouldFetchFullPage
          ),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl)
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return html => {
          this.productModal?.remove();

          const selector = updateFullPage
            ? "product-info[id^='MainProduct']"
            : "product-info";
          const variant = this.getSelectedVariant(
            html.querySelector(selector)
          );
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector("head title").innerHTML =
              html.querySelector("head title").innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector("main"),
              html.querySelector("main"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector("product-info"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then(response => response.text())
          .then(responseText => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch(error => {
            if (error.name === "AbortError") {
              // console.log("Fetch aborted by user");
            } else {
              // console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector(
          "product-variant-selects [data-selected-variant]"
        )?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      hasVariantSelectionState(productInfoNode = this) {
        return Boolean(
          productInfoNode.querySelector(
            "product-variant-selects [data-selected-variant]"
          )
        );
      }

      buildRequestUrlWithParams(
        url,
        optionValues,
        shouldFetchFullPage = false
      ) {
        const params = [];

        !shouldFetchFullPage &&
          params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(",")}`);
        }

        return `${url}?${params.join("&")}`;
      }

      updateOptionValues(html) {
        const productVariantSelects = html.querySelector("product-variant-selects");
        if (productVariantSelects) {
          HTMLUpdateUtility.viewTransition(
            this.variantSelectors,
            productVariantSelects,
            this.preProcessHtmlCallbacks
          );
        }
      }

      handleUpdateProductInfo(productUrl) {
        return html => {
          const variant = this.getSelectedVariant(html);
          this.debugLog('handleUpdateProductInfo', {
            productUrl,
            variantId: variant?.id,
            variantFeaturedMediaId: variant?.featured_media?.id
          });

          // this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);
          this.updateVariantDiscountBadge(variant?.price, variant?.compare_at_price);
          this.updateModalMciPricing(variant?.price, variant?.compare_at_price);
          this.updateVariantSaleBadge(variant?.sku);
          this.updateVariantDetails(variant?.id);
          // Keep pickup availability widgets in sync with selected variant
          if (variant?.id) {
            const pickupAvailabilityWidgets = this.querySelectorAll(
              "pickup-availability, pdp-pickup-availability"
            );
            pickupAvailabilityWidgets.forEach((widget) => {
              if (typeof widget.setVariantId === "function") {
                widget.setVariantId(variant.id);
                return;
              }
              widget.dataset.variantId = variant.id;
              if (typeof widget.fetchAvailability === "function") {
                widget.fetchAvailability(variant.id);
              }
            });
          }

          let filteredMediaId = null;
          if (this.productMediaGallery && this.productMediaGallery.dataset.hideOtherVariantsMedia === 'true') {
            filteredMediaId = this.updateVariantMedia();
            this.debugLog('filtered media result', { filteredMediaId });
          }

          if (!variant) {
            this.setUnavailable();
            this.updatePdpGalleryEntregaTag(null);
            this.updatePdpTitleForColorVariant(null);
            return;
          }

          this.updateMedia(filteredMediaId || variant?.featured_media?.id);

          const updateSourceFromDestination = (
            id,
            shouldHide = source => false
          ) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);

            if (source && destination) {

              destination.innerHTML = source.innerHTML;

              if (id === 'Inventory') {
                const inventoryThreshold = parseInt(source.dataset.inventoryThreshold);
                const inventoryQuantity = parseInt(source.dataset.inventoryQuantity);
                destination.classList.toggle("hidden", inventoryThreshold && inventoryQuantity > inventoryThreshold || inventoryQuantity < 1);
              } else {
                destination.classList.toggle("hidden", shouldHide(source));
              }
            }
          };

          updateSourceFromDestination("ProductPurchaseInfo");
          updateSourceFromDestination("ProductStickyCart");

          updateSourceFromDestination("price");
          this.updatePdpGalleryEntregaTag(variant?.id);
          updateSourceFromDestination("Sku", ({ classList }) => classList.contains("hidden"));

          updateSourceFromDestination("Inventory", ({ innerText }) => innerText === "");

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(
            `#Quantity-Rules-${this.dataset.section}`
          )?.classList.remove("hidden");

          this.updateAddToCartVisibility(variant);

          if (!isZeroPriceVariant(variant)) {
            this.productForm?.toggleSubmitButton(
              html
                .getElementById(`ProductSubmitButton-${this.sectionId}`)
                ?.hasAttribute("disabled") ?? true,
              window.variantStrings.soldOut
            );
          }

          this.updatePdpTitleForColorVariant(variant);

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant
            }
          });
        };
      }

      updateVariantMedia() {
        if (!this.productMediaGallery) return;

        const selectedMediaGroup = this.getSelectedMediaGroupHandle();
        if (!selectedMediaGroup) return;
        this.debugLog('updateVariantMedia start', {
          selectedMediaGroup
        });

        this.productMediaGallery.dataset.selectedMediaGroup = selectedMediaGroup;

        const gallerySlides = Array.from(
          this.productMediaGallery.querySelectorAll(
            '.main-product__media--slider-wrapper .swiper-slide[data-media-id][data-media-group]'
          )
        );
        const thumbsSlides = Array.from(
          this.productMediaGallery.querySelectorAll(
            '.main-product__media--thumbs-wrapper .swiper-slide[data-media-id][data-media-group]'
          )
        );
        const gridSlides = Array.from(
          this.productMediaGallery.querySelectorAll('.main-product__media--grid .swiper-slide[data-media-group]')
        );

        const mediaIdsByFeaturedRange = this.getMediaIdsByFeaturedRange(selectedMediaGroup, gallerySlides);
        gallerySlides.forEach((slide) => {
          const shouldHideByGroup = slide.dataset.mediaGroup !== selectedMediaGroup;
          const shouldHideByRange =
            mediaIdsByFeaturedRange instanceof Set
              ? !mediaIdsByFeaturedRange.has(String(slide.dataset.mediaId))
              : shouldHideByGroup;
          slide.classList.toggle('hidden', shouldHideByRange);
        });

        // Keep thumbs perfectly aligned with gallery by media-id to avoid desync counts.
        const visibleGalleryMediaIds = new Set(
          gallerySlides
            .filter((slide) => !slide.classList.contains('hidden'))
            .map((slide) => String(slide.dataset.mediaId))
        );
        thumbsSlides.forEach((slide) => {
          slide.classList.toggle('hidden', !visibleGalleryMediaIds.has(String(slide.dataset.mediaId)));
        });

        gridSlides.forEach((slide) => {
          slide.classList.toggle('hidden', slide.dataset.mediaGroup !== selectedMediaGroup);
        });

        const visibleGallerySlides = gallerySlides.filter((slide) => !slide.classList.contains('hidden')).length;
        const visibleThumbSlides = thumbsSlides.filter((slide) => !slide.classList.contains('hidden')).length;
        this.debugLog('updateVariantMedia visibility', {
          visibleGallerySlides,
          visibleThumbSlides,
          gallerySlidesTotal: gallerySlides.length,
          thumbsSlidesTotal: thumbsSlides.length
        });

        const thumbsElement = this.productMediaGallery.querySelector('swiper-product-thumbs[handle="product-thumbs"]');
        const galleryElement = this.productMediaGallery.querySelector('swiper-product-gallery[handle="product-gallery"]');

        if (thumbsElement) {
          if (thumbsElement.instance) {
            thumbsElement.instance.update();
          } else {
            thumbsElement.init();
          }
        }

        if (galleryElement) {
          if (galleryElement.instance) {
            galleryElement.instance.update();
            const thumbsSwiper = thumbsElement?.instance;
            if (thumbsSwiper && galleryElement.instance.thumbs) {
              galleryElement.instance.thumbs.swiper = thumbsSwiper;
              galleryElement.instance.thumbs.init();
              galleryElement.instance.thumbs.update(true);
            }
          } else {
            galleryElement.init();
          }
        }

        const productGallery = this.productMediaGallery.querySelector('swiper-product-gallery[handle="product-gallery"]');
        const firstVisibleMediaId = Number(
          gallerySlides.find((slide) => !slide.classList.contains('hidden'))?.dataset?.mediaId
        );
        this.debugLog('updateVariantMedia first visible', { firstVisibleMediaId });

        return firstVisibleMediaId || null;
      }

      getAllVariantsData() {
        if (this._allVariantsData) return this._allVariantsData;
        const variantsJson = this.productVariantSelects?.querySelector('[data-all-variants]')?.textContent?.trim();
        if (!variantsJson) return null;
        try {
          this._allVariantsData = JSON.parse(variantsJson);
        } catch (error) {
          this.debugLog('getAllVariantsData parse error', { error });
          return null;
        }
        return this._allVariantsData;
      }

      getMediaIdsByFeaturedRange(selectedMediaGroup, gallerySlides) {
        const mediaFilterOptionPosition = Number(this.productMediaGallery?.dataset.mediaFilterOptionPosition || 0);
        if (!mediaFilterOptionPosition || !gallerySlides?.length) return null;

        const allVariants = this.getAllVariantsData();
        if (!Array.isArray(allVariants) || !allVariants.length) return null;

        const groupToFeaturedMediaId = new Map();
        allVariants.forEach((variant) => {
          const optionValue = variant?.options?.[mediaFilterOptionPosition - 1];
          const optionHandle = optionValue ? this.toMediaGroupHandle(optionValue) : null;
          const featuredMediaId = Number(variant?.featured_media?.id);
          if (!optionHandle || !featuredMediaId || groupToFeaturedMediaId.has(optionHandle)) return;
          groupToFeaturedMediaId.set(optionHandle, featuredMediaId);
        });

        if (!groupToFeaturedMediaId.size || !groupToFeaturedMediaId.has(selectedMediaGroup)) return null;

        const orderedGroups = Array.from(groupToFeaturedMediaId.entries())
          .map(([groupHandle, mediaId]) => {
            const startIndex = gallerySlides.findIndex((slide) => Number(slide.dataset.mediaId) === mediaId);
            return { groupHandle, mediaId, startIndex };
          })
          .filter((item) => item.startIndex > -1)
          .sort((a, b) => a.startIndex - b.startIndex);

        const selectedGroupPosition = orderedGroups.findIndex((item) => item.groupHandle === selectedMediaGroup);
        if (selectedGroupPosition < 0) return null;

        const startIndex = orderedGroups[selectedGroupPosition].startIndex;
        const endIndex =
          selectedGroupPosition < orderedGroups.length - 1
            ? orderedGroups[selectedGroupPosition + 1].startIndex
            : gallerySlides.length;

        const mediaIds = new Set(
          gallerySlides.slice(startIndex, endIndex).map((slide) => String(slide.dataset.mediaId))
        );

        this.debugLog('featured-range mapping', {
          selectedMediaGroup,
          startIndex,
          endIndex,
          mediaIdsCount: mediaIds.size
        });

        return mediaIds;
      }

      getSelectedMediaGroupHandle() {
        const mediaFilterOptionPosition = Number(this.productMediaGallery?.dataset.mediaFilterOptionPosition || 0);
        if (!mediaFilterOptionPosition) return null;

        const selectedVariant = this.getSelectedVariant(this);
        const selectedOptionValue = selectedVariant?.options?.[mediaFilterOptionPosition - 1];
        if (selectedOptionValue) {
          const handle = this.toMediaGroupHandle(selectedOptionValue);
          this.debugLog('selected media group from variant', {
            mediaFilterOptionPosition,
            selectedOptionValue,
            handle
          });
          return handle;
        }

        const fallbackSelectedMediaGroup = this.productMediaGallery?.dataset.selectedMediaGroup;
        return fallbackSelectedMediaGroup || null;
      }

      toMediaGroupHandle(value) {
        return String(value)
          .trim()
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');
      }

      updateVariantOptionMedia() {
        this.productMediaGallery.querySelectorAll('[data-media-group]:not(.hidden)').forEach(element => {

        });
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach(productForm => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? "";
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector("share-button")?.updateUrl(
          `${window.shopUrl}${url}${
            variantId ? `?variant=${variantId}` : ""
          }`
        );

        if (this.dataset.updateUrl === "false") return;
        window.history.replaceState(
          {},
          "",
          `${url}${variantId ? `?variant=${variantId}` : ""}`
        );
      }

      updateSellingPlanURL(sellingPlanId, selectedVariantId) {
        if (!selectedVariantId) return;
        if (!sellingPlanId) {
          window.history.replaceState({}, "", `${window.location.href.replace(/&selling_plan=\d+/, "")}`);
        } else {
          const params = new URLSearchParams(window.location.search);
          params[params.has("variant") ? "set" : "append"](
            "variant",
            selectedVariantId
          );
          if (sellingPlanId) {
            params[params.has("selling_plan") ? "set" : "append"](
              "selling_plan",
              sellingPlanId
            );
          } else {
            params.delete("selling_plan");
          }
          window.history.replaceState({}, "", `${this.dataset.url}?${params.toString()}`);
        }
      }

      updatePdpGalleryEntregaTag(variantId) {
        if (!this.productMediaGallery) return;

        const tags = this.productMediaGallery.querySelectorAll("[data-pdp-entrega-tag]");
        if (!tags.length) return;

        const priceContainer = this.querySelector(".price__container");
        if (!priceContainer) {
          tags.forEach((tag) => tag.classList.add("hidden"));
          return;
        }

        const enabled = priceContainer.dataset.messageCustomEnabled === "true";
        const html = priceContainer.dataset.messageCustomHtml || "";
        const color = priceContainer.dataset.messageCustomColor || "#374151";
        const sizeMobile = priceContainer.dataset.messageCustomSizeMobile || "12";
        const sizeDesktop = priceContainer.dataset.messageCustomSizeDesktop || "14";
        const entregaSet = new Set(
          String(priceContainer.dataset.messageCustomEntregaVariantIds || "")
            .split(",")
            .map((id) => id.trim())
            .filter(Boolean)
        );

        const show =
          Boolean(enabled && html && variantId && entregaSet.has(String(variantId)));

        tags.forEach((tag) => {
          tag.classList.toggle("hidden", !show);
          if (!show) return;

          // Evita innerHTML repetido (mismo mensaje en todas las variantes con entrega): menos trabajo de parseo en el hilo principal.
          if (tag.innerHTML !== html) {
            tag.innerHTML = html;
          }
          tag.style.setProperty("--message-custom-color", color);
          tag.style.setProperty(
            "--message-custom-size-mobile",
            `${sizeMobile}px`
          );
          tag.style.setProperty(
            "--message-custom-size-desktop",
            `${sizeDesktop}px`
          );
          tag.style.color = "var(--message-custom-color)";
        });
      }

      updatePdpTitleForColorVariant(variant) {
        if (!this.hasAttribute("data-pdp-color-option-index")) return;

        const colorIndex = parseInt(this.dataset.pdpColorOptionIndex, 10);
        if (!Number.isInteger(colorIndex) || colorIndex < 1) return;

        const colorRaw = variant ? variant[`option${colorIndex}`] : null;
        const colorValue =
          colorRaw != null && String(colorRaw).trim() !== "" ? String(colorRaw).trim() : "";

        const nodes = this.querySelectorAll("[data-pdp-title-base]");
        if (nodes.length === 0) return;

        nodes.forEach((el) => {
          const base = el.getAttribute("data-pdp-title-base") || "";
          const next = colorValue ? `${base} ${colorValue}`.trim() : base;
          if (el.textContent !== next) {
            el.textContent = next;
          }
        });
      }

      formatDashBulletListHtml(text) {
        if (text == null || text === '') return '';

        let plain = String(text)
          .replace(/<[^>]*>/g, ' ')
          .replace(/&nbsp;/gi, ' ')
          .replace(/\s+/g, ' ')
          .trim();

        plain = plain.replace(/^\s*-\s*/, '');

        if (!/\s+-/.test(plain)) {
          return this.visualStockEscapeHtml(plain).replace(/\n/g, '<br />');
        }

        const parts = plain
          .split(/\s+-/)
          .map((part) => part.replace(/^\s*-\s*/, '').trim())
          .filter(Boolean);

        if (parts.length <= 1) {
          return this.visualStockEscapeHtml(plain).replace(/\n/g, '<br />');
        }

        return (
          '<ul class="product-content-dash-list">' +
          parts.map((item) => `<li>${this.visualStockEscapeHtml(item)}</li>`).join('') +
          '</ul>'
        );
      }

      updateVariantDetails(variantId) {
        const productVariantDetails = this.querySelectorAll('.product__metafield_for_variant-block');
        if (productVariantDetails) {
          productVariantDetails.forEach((productVariantDetail) => {
            const block_id = productVariantDetail.getAttribute('data-block-id');
            const metafieldDataElement = this.querySelector(`#product__metafield_for_variant-block--${block_id}`);
            if (!metafieldDataElement) {
              return;
            }
            try {
              let productVariantDetailsData = JSON.parse(metafieldDataElement?.textContent);
              const variantText = productVariantDetailsData?.[String(variantId)] ?? '';

              if (productVariantDetail.hasAttribute('data-format-dash-list')) {
                productVariantDetail.innerHTML = this.formatDashBulletListHtml(variantText);
              } else {
                productVariantDetail.innerHTML = this.visualStockEscapeHtml(String(variantText)).replaceAll('\n', '<br />');
              }
            } catch (error) {
              console.error('Error parsing JSON', error);
            }
          });
        }

        this.updateVisualStockBlocks(variantId);
      }

      visualStockSettingEnabled(value) {
        return value === true || value === 'true';
      }

      visualStockEscapeHtml(value) {
        return String(value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;');
      }

      visualStockStyleClasses({ bold = false, italic = false, baseClass = '' }) {
        let classes = baseClass;
        if (bold) classes += ' product-visual-stock__value--bold';
        if (italic) classes += ' product-visual-stock__value--italic';
        return classes.trim();
      }

      buildVisualStockMessageHtml(template, min, max, blockEl) {
        const textClass = this.visualStockStyleClasses({
          baseClass: 'product-visual-stock__text',
          bold: this.visualStockSettingEnabled(blockEl.dataset.messageBold),
          italic: this.visualStockSettingEnabled(blockEl.dataset.messageItalic)
        });
        const minClass = this.visualStockStyleClasses({
          baseClass: 'product-visual-stock__value product-visual-stock__value--min',
          bold: this.visualStockSettingEnabled(blockEl.dataset.stockMinBold),
          italic: this.visualStockSettingEnabled(blockEl.dataset.stockMinItalic)
        });
        const maxClass = this.visualStockStyleClasses({
          baseClass: 'product-visual-stock__value product-visual-stock__value--max',
          bold: this.visualStockSettingEnabled(blockEl.dataset.stockMaxBold),
          italic: this.visualStockSettingEnabled(blockEl.dataset.stockMaxItalic)
        });

        const pieces = template.split(/(\[STOCK_MIN\]|\[STOCK_MAX\])/);

        return pieces
          .map((piece) => {
            if (piece === '[STOCK_MIN]') {
              return `<span class="${minClass}">${this.visualStockEscapeHtml(min)}</span>`;
            }
            if (piece === '[STOCK_MAX]') {
              return `<span class="${maxClass}">${this.visualStockEscapeHtml(max)}</span>`;
            }
            if (!piece) return '';
            return `<span class="${textClass}">${this.visualStockEscapeHtml(piece)}</span>`;
          })
          .join('');
      }

      updateVisualStockBlocks(variantId) {
        const blocks = this.querySelectorAll('[data-product-visual-stock]');
        if (!blocks.length) return;

        blocks.forEach((blockEl) => {
          const blockId = blockEl.getAttribute('data-block-id');
          const dataEl = this.querySelector(`#product-visual-stock-data--${blockId}`);
          const messageEl = blockEl.querySelector('[data-visual-stock-message]');
          const template = blockEl.getAttribute('data-message-template') || '';

          if (!dataEl || !messageEl) return;

          let stockByVariant = {};
          try {
            stockByVariant = JSON.parse(dataEl.textContent || '{}');
          } catch (error) {
            console.error('Error parsing visual stock JSON', error);
            blockEl.hidden = true;
            blockEl.setAttribute('aria-hidden', 'true');
            return;
          }

          const stock = stockByVariant?.[String(variantId)];
          const outerBlock = blockEl.closest('.product__visual-stock');

          if (!stock || stock.min == null || stock.max == null || stock.min === '' || stock.max === '') {
            blockEl.hidden = true;
            blockEl.setAttribute('aria-hidden', 'true');
            blockEl.classList.add('product-visual-stock--empty');
            if (outerBlock) {
              outerBlock.hidden = true;
              outerBlock.setAttribute('aria-hidden', 'true');
            }
            return;
          }

          blockEl.hidden = false;
          blockEl.removeAttribute('aria-hidden');
          blockEl.classList.remove('product-visual-stock--empty');
          if (outerBlock) {
            outerBlock.hidden = false;
            outerBlock.removeAttribute('aria-hidden');
          }

          messageEl.innerHTML = this.buildVisualStockMessageHtml(
            template,
            stock.min,
            stock.max,
            blockEl
          );
        });
      }

      updateVariantDiscountBadge(price, compare_at_price) {
        const discountBadge = this.querySelector(".product-card__badge--discount");
        if (!discountBadge) return;


        if (compare_at_price > price) {
          discountBadge.classList.remove("hidden");
          const percentageElement = discountBadge.querySelector(".percentage");

          if (!percentageElement) return;
          // calculate discount percentage
          const discountPercentage = Math.floor(
            ((compare_at_price - price) / compare_at_price) * 100
          );
          // update discount percentage
          percentageElement.textContent = discountPercentage;
        } else {
          discountBadge.classList.add("hidden");
        }
      }

      updateModalMciPricing(price, compareAtPrice) {
        if (!this.hasVariantSelectionState()) return;

        const modalMci = this.querySelector(".modal-mci");
        const discountedPriceElements = this.querySelectorAll("[data-price-discounted]");
        const priceContainer = this.querySelector(".price__container");
        const moneyFormat =
          priceContainer?.dataset?.moneyFormat ||
          window?.Shopify?.money_format ||
          "${{amount}}";

        if (price === null || price === undefined) {
          discountedPriceElements.forEach((element) => {
            element.textContent = "";
          });
          if (modalMci) {
            modalMci.querySelector("[data-mci-price]")?.replaceChildren();
            modalMci.querySelector("[data-mci-monthly]")?.replaceChildren();
            modalMci.querySelector("[data-mci-total-discount]")?.classList.add("hidden");
          }
          return;
        }

        const numericPrice = Number(price);
        const cashPrice = Math.round(numericPrice * 0.9);
        const formattedCashPrice = formatPrice(cashPrice, moneyFormat);

        discountedPriceElements.forEach((element) => {
          element.textContent = formattedCashPrice;
        });

        if (!modalMci) return;

        const priceEl = modalMci.querySelector("[data-mci-price]");
        const monthlyEl = modalMci.querySelector("[data-mci-monthly]");
        const totalDiscountEl = modalMci.querySelector("[data-mci-total-discount]");

        if (priceEl) {
          priceEl.textContent = formatPrice(numericPrice, moneyFormat);
        }

        if (monthlyEl) {
          monthlyEl.textContent = formatPrice(numericPrice / 12, moneyFormat);
        }

        if (totalDiscountEl) {
          const compareAt = Number(compareAtPrice);
          let totalDiscountPercentage = 0;

          if (compareAt > numericPrice) {
            totalDiscountPercentage = Math.floor(
              ((compareAt - cashPrice) / compareAt) * 100
            );
          } else if (numericPrice > cashPrice) {
            totalDiscountPercentage = Math.floor(
              ((numericPrice - cashPrice) / numericPrice) * 100
            );
          }

          if (totalDiscountPercentage > 0) {
            totalDiscountEl.textContent = `${totalDiscountPercentage}% OFF`;
            totalDiscountEl.classList.remove("hidden");
          } else {
            totalDiscountEl.textContent = "";
            totalDiscountEl.classList.add("hidden");
          }
        }
      }

      updateVariantDiscountedCashPrice(price) {
        this.updateModalMciPricing(price, this.getSelectedVariant(this)?.compare_at_price);
      }

      updateVariantSaleBadge(variantSku) {
        if (!this._saleSkuBadges?.length) return;
        const normalizedSku = this.normalizeSku(variantSku);
        const showSaleBadge = normalizedSku && this._saleBadgeSkuSet.has(normalizedSku);
        this._saleSkuBadges.forEach((badge) => {
          badge.classList.toggle("sale-sku-badge--hidden", !showSaleBadge);
        });
      }

      normalizeSku(value) {
        return String(value || "").trim().toUpperCase();
      }

      getSaleBadgeSkuSet() {
        if (this._cachedSaleBadgeSkuSet) return this._cachedSaleBadgeSkuSet;
        const saleBadgeConfig = window.gaiaSaleBadgeConfig || {};
        if (saleBadgeConfig.enabled !== true) {
          this._cachedSaleBadgeSkuSet = new Set();
          return this._cachedSaleBadgeSkuSet;
        }
        const skusRaw = String(saleBadgeConfig.skusRaw || "");
        this._cachedSaleBadgeSkuSet = new Set(
          skusRaw
            .split(/[\n,\r]+/g)
            .map((sku) => this.normalizeSku(sku))
            .filter(Boolean)
        );
        return this._cachedSaleBadgeSkuSet;
      }

      setUnavailable() {
        this.updateAddToCartVisibility(null);
        this.productForm?.toggleSubmitButton(
          true,
          window.variantStrings.unavailable
        );

        const selectors = [
          "price",
          "Inventory",
          "Sku",
          "Quantity-Rules"
        ]
          .map(id => `#${id}-${this.dataset.section}`)
          .join(", ");
        document
          .querySelectorAll(selectors)
          .forEach(({ classList }) => classList.add("hidden"));
      }

      updateAddToCartVisibility(variant) {
        const hide = isZeroPriceVariant(variant);
        const sectionKey = this.sectionId || this.dataset.section;

        const controlSelector =
          'button[type="submit"], .quick-cart-drawer__trigger, .product-bar__submit, .shopify-payment-button';

        const toggleControls = (container) => {
          if (!container) return;

          const isDirectControl = container.matches(controlSelector);

          if (!isDirectControl) {
            container.classList.toggle("hidden", hide);
            container.setAttribute("aria-hidden", hide ? "true" : "false");
          }

          const controls = isDirectControl
            ? [container]
            : container.querySelectorAll(controlSelector);

          controls.forEach((control) => {
            if (isDirectControl || !control.matches("button")) {
              control.classList.toggle("hidden", hide);
            }
            control.setAttribute("aria-hidden", hide ? "true" : "false");
            if (control.matches("button")) {
              control.toggleAttribute("disabled", hide);
            }
            control
              .querySelectorAll("button, .shopify-payment-button__button")
              .forEach((nestedControl) => {
                nestedControl.toggleAttribute("disabled", hide);
              });
          });
        };

        this.querySelectorAll("[data-product-add-to-cart]").forEach(toggleControls);

        toggleControls(
          document
            .getElementById(`ProductStickyCart-${sectionKey}`)
            ?.querySelector("[data-product-add-to-cart]")
        );

        if (hide) {
          this.productForm?.toggleSubmitButton(true);
        }
      }

      updateMedia(variantFeaturedMediaId) {
        const shouldUseFirstVisibleMedia =
          this.productMediaGallery?.dataset.hideOtherVariantsMedia === 'true';
        this.debugLog('updateMedia called', {
          variantFeaturedMediaId,
          shouldUseFirstVisibleMedia
        });

        let targetMediaId = variantFeaturedMediaId;
        if (shouldUseFirstVisibleMedia) {
          const firstVisibleMedia = this.productMediaGallery
            ?.querySelector('swiper-product-gallery[handle="product-gallery"]')
            ?.querySelector('.swiper-slide[data-media-id][data-media-group]:not(.hidden)');

          if (firstVisibleMedia?.dataset?.mediaId) {
            targetMediaId = Number(firstVisibleMedia.dataset.mediaId);
          }
        }

        if (!targetMediaId) return;
        this.debugLog('updateMedia resolved target', { targetMediaId });

        // if layout is grid, add .active class to the relevant swiper-slide
        const gridLayout = this.querySelector('.main-product__media--grid');
        if (gridLayout) {
          // remove active class from all swiper-slides
          gridLayout.querySelectorAll('.swiper-slide').forEach(item => {
            item.classList.remove('active');
          });
          // add .active class to the swiper-slide with the matching data-media-id
          const activeSlide = gridLayout.querySelector(`.swiper-slide[data-media-id="${targetMediaId}"]`);
          if (activeSlide) {
            activeSlide.classList.add('active');
          }
        }

        if (
          this.querySelector(".main-product__media")?.hasAttribute(
            "data-desktop-grid"
          ) && !DeviceDetector.isMobile()
        ) {
          this.scrollToImage(targetMediaId);
        }

        const productMedia = this.querySelector(
          "swiper-product-gallery"
        );
        if (shouldUseFirstVisibleMedia) {
          requestAnimationFrame(() => {
            productMedia?.setActiveMedia(targetMediaId, { pinFirstVisible: true });
          });
        } else {
          productMedia?.setActiveMedia(targetMediaId);
        }
      }

      scrollToImage(variantFeaturedMediaId) {
        this.querySelectorAll("[data-media-id]").forEach(img => {
          if (
            img.getAttribute("data-media-id") ==
            variantFeaturedMediaId
          ) {
            img.scrollIntoView({
              block: "end",
              behavior: "smooth"
            });
          }
        });
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity
            ? parseInt(this.quantityInput.dataset.cartQuantity)
            : 0,
          min: this.quantityInput.dataset.min
            ? parseInt(this.quantityInput.dataset.min)
            : 1,
          max: this.quantityInput.dataset.max
            ? parseInt(this.quantityInput.dataset.max)
            : null,
          step: this.quantityInput.step
            ? parseInt(this.quantityInput.step)
            : 1
        };

        let min = data.min;
        const max =
          data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min)
          min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute("max");
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId =
          this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector(
          ".quantity__rules-cart .loading__spinner"
        ).classList.remove("hidden");
        fetch(
          `${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`
        )
          .then(response => response.text())
          .then(responseText => {
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch(e => console.error(e))
          .finally(() =>
            this.querySelector(
              ".quantity__rules-cart .loading__spinner"
            ).classList.add("hidden")
          );
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(
          `Quantity-Form-${sectionId}`
        );
        const selectors = [
          ".quantity__input",
          ".quantity__rules",
          ".quantity__label"
        ];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === ".quantity__input") {
            const attributes = [
              "data-cart-quantity",
              "data-min",
              "data-max",
              "step"
            ];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(
          `#ProductModal-${this.dataset.section}`
        );
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector("product-variant-selects");
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "related-products"
        );
        return document.querySelector(
          `product-recommendations[data-section-id^="${relatedProductsSectionId}"]`
        );
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "quick_order_list"
        );
        return document.querySelector(
          `quick-order-list[data-id^="${quickOrderListSectionId}"]`
        );
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}

if (!customElements.get('product-form')) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        this.variantIdInput.disabled = false;
        this.form.addEventListener(
          "submit",
          this.onSubmitHandler.bind(this)
        );
        this.cart = document.querySelector("cart-notification") || document.querySelector("cart-drawer");
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText =
          this.submitButton.querySelector("span");

        if (document.querySelector("cart-drawer"))
          this.submitButton.setAttribute("aria-haspopup", "dialog");

        this.hideErrors = this.dataset.hideErrors === "true";
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (
          this.submitButton.getAttribute("aria-disabled") === "true" ||
          this.submitButton.hasAttribute("disabled") ||
          this.closest("[data-product-add-to-cart]")?.classList.contains("hidden")
        )
          return;

        const selectedVariantNode = this.closest("product-info")?.querySelector(
          "product-variant-selects [data-selected-variant]"
        );
        if (selectedVariantNode?.textContent) {
          try {
            const selectedVariant = JSON.parse(selectedVariantNode.textContent);
            if (isZeroPriceVariant(selectedVariant)) return;
          } catch (_error) {}
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("loading");
        this.querySelector(".loading__spinner").classList.remove(
          "hidden"
        );

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData = new FormData(this.form);

        if (this.hasAttribute('data-has-pre-order')) {
          formData.append('properties[Pre-order]', '✓');
        }
        if (this.cart) {
          formData.append(
            "sections",
            this.cart
              .getSectionsToRender()
              .map(section => section.section)
          );
          formData.append("sections_url", window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then(response => response.json())
          .then(response => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: formData.get("id"),
                errors: response.errors || response.description,
                message: response.message
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector(
                ".sold-out-message"
              );
              if (!soldOutMessage) return;
              this.submitButton.setAttribute("aria-disabled", true);
              this.submitButtonText.classList.add("hidden");
              soldOutMessage.classList.remove("hidden");
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "product-form",
                productVariantId: formData.get("id"),
                cartData: response
              });
            this.error = false;
            this.cart.renderContents(response);
          })
          .catch(e => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove("loading");
            if (this.cart && this.cart.classList.contains("is-empty"))
              this.cart.classList.remove("is-empty");
            if (!this.error)
              this.submitButton.removeAttribute("aria-disabled");
            this.querySelector(".loading__spinner").classList.add(
              "hidden"
            );
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message"
          );

        this.errorMessageWrapper.toggleAttribute(
          "hidden",
          !errorMessage
        );

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute("disabled", "disabled");
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute("disabled");
          this.submitButtonText.textContent = this.hasAttribute(
            "data-has-selling-plan"
          )
            ? window.variantStrings.addSubscriptionToCart
            : this.submitButtonText.textContent = this.hasAttribute(
                "data-has-pre-order"
              )
            ?  window.variantStrings.preOrder
            : window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector("[name=id]");
      }
    }
  );
}

document.querySelectorAll('.product__content > *').forEach((el) => {
  if (el.classList.contains('product__pickup-availabilities')  || el.classList.contains('product__block--custom-liquid')) return;
  if (!el.textContent.trim()) {
    el.textContent = '';
  }
});
